import CodeMirror from '@uiw/react-codemirror';
import { html } from '@codemirror/lang-html';
import { EditorView } from '@codemirror/view';
import { WrapText } from 'lucide-react';
import { formatHtml } from '../../lib/format-html';

interface HtmlCodeEditorProps {
  value: string;
  onChange: (value: string) => void;
  readOnly?: boolean;
  /** Editor height, e.g. "16rem". Defaults to a comfortable ~8-line box. */
  height?: string;
  id?: string;
}

// n8n writes its drafts as one very long line. Wrapping makes that readable
// without touching the content; the Format button re-indents it, and is
// deliberately a button rather than automatic - see lib/format-html.ts.
const EXTENSIONS = [html(), EditorView.lineWrapping];

// Syntax-highlighting HTML editor for the email-campaign body — line numbers,
// bracket matching, highlighting. It edits the raw HTML (no WYSIWYG rewriting),
// so the inline styles email clients need survive byte-for-byte.
export function HtmlCodeEditor({ value, onChange, readOnly = false, height = '16rem', id }: HtmlCodeEditorProps) {
  // Match the admin's active theme by reading the `.dark` root class (the same
  // signal Tailwind's dark: variants use), rather than depending on the theme
  // context — keeps this primitive usable anywhere without a provider.
  // ponytail: evaluated per render, so a theme toggle while the editor is open
  // updates on the next re-render, not instantly — fine for an edit dialog.
  const isDark = typeof document !== 'undefined' && document.documentElement.classList.contains('dark');
  const isFormatted = value.includes('\n');

  return (
    <div className="overflow-hidden rounded-md border border-input">
      {!readOnly && (
        <div className="flex items-center justify-end border-b border-input bg-muted/50 px-2 py-1">
          <button
            type="button"
            onClick={() => onChange(formatHtml(value))}
            disabled={!value.trim() || isFormatted}
            title={isFormatted ? 'Already on multiple lines' : 'Indent this HTML onto separate lines'}
            className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-xs font-medium text-muted-foreground hover:bg-accent hover:text-foreground disabled:opacity-40 disabled:hover:bg-transparent"
          >
            <WrapText className="h-3.5 w-3.5" />
            Format
          </button>
        </div>
      )}
      <div id={id} className="text-xs">
        <CodeMirror
          value={value}
          onChange={onChange}
          readOnly={readOnly}
          editable={!readOnly}
          height={height}
          theme={isDark ? 'dark' : 'light'}
          extensions={EXTENSIONS}
          basicSetup={{ lineNumbers: true, foldGutter: false, highlightActiveLine: !readOnly }}
        />
      </div>
    </div>
  );
}
